import { FC, useState } from "react";
import { ChakraStylesConfig, Select, SingleValue } from "chakra-react-select";
import { useField } from "formik";
import { InputWrapperSimple } from "~/components";

export interface AutocompleteOption {
  value: number;
  label: string;
}

interface Props {
  id: string;
  isDisabled?: boolean;
  label?: string;
  minChars?: number;
  options: AutocompleteOption[];
  placeholder?: string;
}

// Mirrors the `th-short-text` Input variant so the type-ahead lines up with the
// plain text fields it sits alongside (full width, same border/radius/focus).
const chakraStyles: ChakraStylesConfig<AutocompleteOption, false> = {
  container: (provided) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided, state) => ({
    ...provided,
    width: "100%",
    minH: "3rem",
    bg: "bg-main",
    backgroundColor: "bg-main",
    borderWidth: "1px",
    borderColor: state.isFocused ? "th.actionLavender2" : "th.cream3",
    borderRadius: "lg",
    boxShadow: state.isFocused ? "0px 1px 2px 0px var(--chakra-colors-newLavender-100)" : "none",
    color: "fg-main",
    fontFamily: "Moderat",
    fontWeight: "light",
    fontSize: "1.125rem",
    _hover: {
      borderColor: state.isFocused ? "th.actionLavender2" : "th.cream3",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    px: 4,
  }),
  input: (provided) => ({
    ...provided,
    color: "fg-main",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "fg-main",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    bg: "bg-main",
    color: "fg-main",
  }),
  // The menu popover needs an opaque surface (and to sit above sibling fields)
  // so the question/input below does not bleed through the suggestions.
  menu: (provided) => ({
    ...provided,
    zIndex: 2,
    bg: "bg-main",
    border: "1px solid",
    borderColor: "th.cream3",
    borderRadius: "lg",
    boxShadow: "md",
    overflow: "hidden",
  }),
  menuList: (provided) => ({
    ...provided,
    bg: "bg-main",
  }),
  option: (provided, state) => ({
    ...provided,
    bg: state.isFocused ? "th.cream3" : "bg-main",
    color: "fg-main",
    fontFamily: "Moderat",
    fontWeight: "light",
    fontSize: "1.125rem",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "th.gray4",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
};

// Type-ahead select that only surfaces suggestions once the user has typed
// `minChars` characters, keeping a large option list (e.g. every clinic) usable.
export const AutocompleteInput: FC<Props> = ({ id, isDisabled, label, minChars = 3, options, placeholder }) => {
  const [field, meta, { setValue, setTouched }] = useField<number | null>(id);
  const [inputValue, setInputValue] = useState("");
  const type = "autocomplete";
  const inputId = `${type}-input-${id}`;

  const selectedOption = options.find((option) => option.value === field.value) ?? null;

  const handleChange = (option: SingleValue<AutocompleteOption>) => {
    setValue(option?.value ?? null);
  };

  return (
    <InputWrapperSimple label={label} meta={meta} id={`${inputId}-field`}>
      <Select<AutocompleteOption>
        chakraStyles={chakraStyles}
        id={inputId}
        inputId={`${inputId}-field`}
        instanceId={inputId}
        isClearable
        isDisabled={isDisabled}
        menuIsOpen={inputValue.length >= minChars}
        menuPortalTarget={document.body}
        onBlur={() => setTouched(true)}
        onChange={handleChange}
        onInputChange={(value) => setInputValue(value)}
        options={options}
        placeholder={placeholder}
        size="lg"
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        value={selectedOption}
      />
    </InputWrapperSimple>
  );
};
